"use client";

import { m as motion, useInView, useReducedMotion } from "framer-motion";
import Link from "next/link";
import { useRef, useState } from "react";
import { useLocale } from "next-intl";
import {
  Check,
  X,
  RefreshCcw,
  Code2,
  FileText,
  TestTube,
  PhoneCall,
  MessageSquare,
  ChevronDown,
  Wallet,
  CalendarClock,
  Layers,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ProcessDemo } from "@/components/abbonamento/ProcessDemo";
import { AbbonamentoHeroVisual } from "@/components/abbonamento/AbbonamentoHeroVisual";
import { CostComparison } from "@/components/abbonamento/CostComparison";
import { TaskFlow, type FlowStep } from "@/components/abbonamento/TaskFlow";
import { TaskLog } from "@/components/abbonamento/TaskLog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { InBreve } from "@/components/shared/InBreve";
import { localizedRoutes } from "@/lib/routes/routes";
import { trackEvent } from "@/lib/track";

type PlanKey = "monthly" | "weekly" | "consulting";

/**
 * Companion visivo per la card "In breve": rende la card full-width (via prop
 * `aside`) e organizza il blocco, sintetizzando i fatti chiave dell'abbonamento
 * in modo scansionabile (il testo resta il blocco AEO completo). Solo
 * transform/opacity, entrata one-shot allo scroll.
 */
function InBreveAside({ isItalian }: { isItalian: boolean }) {
  const facts = isItalian
    ? [
        { icon: Wallet, label: "Quota fissa", value: "4.800 € / mese" },
        { icon: CalendarClock, label: "Oppure", value: "1.400 € / settimana" },
        { icon: Layers, label: "Una richiesta alla volta", value: "coda illimitata" },
      ]
    : [
        { icon: Wallet, label: "Flat fee", value: "4.800 € / month" },
        { icon: CalendarClock, label: "Or", value: "1.400 € / week" },
        { icon: Layers, label: "One request at a time", value: "unlimited queue" },
      ];

  return (
    <div className="w-full max-w-sm space-y-3">
      {facts.map((fact, i) => (
        <motion.div
          key={fact.value}
          initial={{ opacity: 0, x: 16 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.45, delay: 0.1 + i * 0.1 }}
          className="flex items-center gap-3 rounded-xl border border-[hsl(var(--border))] bg-surface-hover/40 p-3.5"
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/15">
            <fact.icon className="h-4 w-4 text-primary" aria-hidden />
          </span>
          <span className="min-w-0">
            <span className="block font-mono text-[0.65rem] uppercase tracking-[0.08em] text-muted-foreground">
              {fact.label}
            </span>
            <span className="block text-sm font-semibold text-foreground">
              {fact.value}
            </span>
          </span>
        </motion.div>
      ))}
      <motion.div
        initial={{ opacity: 0, x: 16 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.45, delay: 0.4 }}
        className="rounded-xl border border-primary/40 bg-primary/[0.08] p-3.5 shadow-[0_0_24px_hsl(var(--primary)/0.12)]"
      >
        <div className="mb-1.5 flex items-center gap-2">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/20">
            <CalendarClock className="h-4 w-4 text-primary" aria-hidden />
          </span>
          <span className="rounded-full bg-primary/15 px-2 py-0.5 font-mono text-[0.62rem] font-semibold uppercase tracking-[0.08em] text-primary">
            {isItalian ? "Risparmi 1.800 €" : "Save 1.800 €"}
          </span>
        </div>
        <span className="block text-sm font-semibold text-foreground">
          {isItalian
            ? "Trimestrale · 4.200 € / mese"
            : "Quarterly · 4.200 € / month"}
        </span>
        <span className="block text-xs text-muted-foreground">
          {isItalian
            ? "Impegno 3 mesi, canone mensile anticipato"
            : "3-month commitment, monthly fee in advance"}
        </span>
      </motion.div>
    </div>
  );
}

/**
 * Piccolo "equalizer" che pulsa = il ritmo costante di lavoro/consegna. Riempie
 * lo spazio in fondo alla card sinistra di "Scegli il ritmo di lavoro" e ne
 * richiama il tema. Solo transform (scaleY); reduced-motion -> waveform statica.
 */
function DeliveryRhythm({ isItalian }: { isItalian: boolean }) {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { margin: "-60px" });
  const run = inView && !reduce;
  const bars = [0.4, 0.7, 1, 0.6, 0.85, 0.5, 0.95, 0.65, 0.8, 0.55, 0.9, 0.45];

  return (
    <div ref={ref} className="mt-auto pt-8" aria-hidden>
      <div className="flex h-14 items-end gap-1.5">
        {bars.map((h, i) => (
          <motion.span
            key={i}
            className="flex-1 rounded-full bg-primary/70"
            style={{ height: `${h * 100}%`, transformOrigin: "bottom" }}
            initial={{ scaleY: run ? 0.3 : 1 }}
            animate={run ? { scaleY: [0.3, 1, 0.5, 0.9, 0.35] } : { scaleY: 1 }}
            transition={
              run
                ? { duration: 1.6, repeat: Infinity, ease: "easeInOut", delay: i * 0.08 }
                : { duration: 0 }
            }
          />
        ))}
      </div>
      <p className="mt-4 font-mono text-[0.7rem] uppercase tracking-[0.12em] text-muted-foreground">
        {isItalian
          ? "Ritmo costante · il prodotto avanza ogni settimana"
          : "Steady rhythm · the product moves forward every week"}
      </p>
    </div>
  );
}

/**
 * Ciclo mensile in line-art ciano per la sezione Termini: un anello con i 3 nodi
 * del trimestrale (mesi 1-2-3) e una cometa che orbita in continuo = canone
 * mensile ricorrente + rinnovo. Solo transform (rotate del gruppo cometa),
 * origine al centro; reduced-motion / fuori schermo -> statico.
 */
function TermsCycleVisual({ isItalian }: { isItalian: boolean }) {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { margin: "-60px" });
  const spin = inView && !reduce;
  // 3 mesi del trimestrale, in ordine orario dall'alto. Ogni mese = un canone
  // pagato in anticipo; il pulse "€" scorre mese dopo mese e il ciclo si ripete.
  const PAY = 4.5; // s, durata del giro dei pagamenti
  const months = [
    { deg: -90, label: "1°" },
    { deg: 30, label: "2°" },
    { deg: 150, label: "3°" },
  ];
  const pos = (deg: number, r: number) => {
    const a = (deg * Math.PI) / 180;
    return { x: 100 + r * Math.cos(a), y: 100 + r * Math.sin(a) };
  };

  return (
    <div
      ref={ref}
      aria-hidden
      className="pointer-events-none relative mx-auto aspect-square w-full max-w-[19rem] select-none"
    >
      <div className="absolute inset-8 rounded-full bg-primary/10 blur-3xl" />
      <svg viewBox="0 0 200 200" className="relative h-full w-full">
        <circle cx="100" cy="100" r="78" fill="none" stroke="hsl(var(--border))" strokeWidth="1.5" />
        <circle
          cx="100"
          cy="100"
          r="78"
          fill="none"
          stroke="hsl(var(--primary) / 0.4)"
          strokeWidth="2"
          strokeDasharray="5 9"
          strokeLinecap="round"
        />

        {/* token che orbita in continuo = il ciclo che si ripete */}
        <motion.g
          style={{ transformOrigin: "100px 100px" }}
          animate={spin ? { rotate: 360 } : { rotate: 0 }}
          transition={spin ? { duration: 6, ease: "linear", repeat: Infinity } : { duration: 0 }}
        >
          <circle cx="100" cy="22" r="7" fill="hsl(var(--primary) / 0.25)" />
          <circle cx="100" cy="22" r="3.5" fill="hsl(var(--primary))" />
        </motion.g>

        {/* 3 nodi mese: dentro il simbolo €, fuori l'ordinale. Un anello di
            "pagamento" si espande da un mese al successivo, in sequenza. */}
        {months.map((m, i) => {
          const { x, y } = pos(m.deg, 78);
          const lab = pos(m.deg, 95);
          const start = i / months.length;
          return (
            <g key={m.label}>
              {spin && (
                <motion.circle
                  cx={x}
                  cy={y}
                  r="13"
                  fill="none"
                  stroke="hsl(var(--primary))"
                  strokeWidth="1.5"
                  style={{ transformOrigin: `${x}px ${y}px` }}
                  animate={{ scale: [1, 1, 1.9, 1.9], opacity: [0, 0.7, 0, 0] }}
                  transition={{
                    duration: PAY,
                    times: [0, start + 0.001, Math.min(1, start + 0.2), 1],
                    repeat: Infinity,
                    ease: "easeOut",
                  }}
                />
              )}
              <circle cx={x} cy={y} r="13" fill="hsl(var(--surface))" stroke="hsl(var(--primary) / 0.5)" strokeWidth="1.5" />
              <text x={x} y={y + 4.5} textAnchor="middle" fontSize="13" fontWeight="700" fill="hsl(var(--primary))" fontFamily="ui-monospace, monospace">
                &euro;
              </text>
              <text x={lab.x} y={lab.y + 3} textAnchor="middle" fontSize="8" fontWeight="600" fill="hsl(var(--primary) / 0.7)" fontFamily="ui-monospace, monospace">
                {m.label}
              </text>
            </g>
          );
        })}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-0.5 text-center">
        <RefreshCcw className="h-6 w-6 text-primary" />
        <span className="font-mono text-[0.6rem] uppercase tracking-[0.14em] text-muted-foreground">
          {isItalian ? "Canone mensile" : "Monthly fee"}
        </span>
        <span className="text-sm font-semibold text-foreground">
          {isItalian ? "Anticipato" : "In advance"}
        </span>
        <span className="mt-0.5 font-mono text-[0.55rem] text-muted-foreground/70">
          {isItalian ? "impegno 3 mesi" : "3-month term"}
        </span>
      </div>
    </div>
  );
}

/**
 * Grafica SVG per la CTA finale "Vuoi partire ora?": una monoposto in line-art
 * ciano (proporzioni credibili: muso e ala anteriore, abitacolo con halo,
 * pance, ala posteriore, due ruote) che vibra al minimo sulla linea di
 * partenza, poi scatta verso destra lasciando scie di velocita', infine resetta.
 *
 * SOLO transform/opacity: l'intero gruppo "auto" trasla in x (scatto) con una
 * micro-vibrazione (x sub-pixel) al minimo; le scie compaiono in opacity; le
 * ruote ruotano (transform). Sotto reduced-motion l'auto resta statica alla
 * linea di partenza (nessun loop). Loop gated da useInView (si ferma fuori
 * schermo). Disegno centrato sul telaio per evitare layout shift.
 */
function RaceCarVisual({ isItalian }: { isItalian: boolean }) {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  // Loop gate: gira solo mentre la grafica e' in viewport, fermo fuori schermo
  // e con reduced-motion (statico alla partenza).
  const inView = useInView(ref, { margin: "-60px" });
  const loop = inView && !reduce;
  const S = "hsl(var(--primary))";
  const SURF = "hsl(var(--surface))";

  // Ciclo: ferma -> vibra al minimo -> scatta a destra -> scompare -> riappare
  // alla partenza. Solo translate in x del gruppo intero.
  const LOOP = 3.2;
  const carX = loop ? [0, 0.6, -0.6, 0.5, 4, 150, 150, 0] : 0;
  const carTimes = [0, 0.08, 0.14, 0.2, 0.28, 0.74, 0.86, 1];
  const carOpacity = loop ? [1, 1, 1, 1, 1, 0, 0, 1] : 1;

  // Ruote: girano durante lo scatto (transform rotate, origine sul mozzo).
  const wheelSpin = loop ? [0, 0, 0, 0, 0, 1440] : 0;
  const wheelTimes = [0, 0.08, 0.14, 0.2, 0.28, 0.74];

  return (
    <div
      ref={ref}
      className="pointer-events-none mx-auto mb-6 w-full max-w-xs select-none"
      aria-hidden
    >
      <svg viewBox="0 0 200 80" className="h-auto w-full">
        {/* Asfalto */}
        <line x1="6" y1="66" x2="194" y2="66" stroke="hsl(var(--border))" strokeWidth="2" strokeLinecap="round" />
        {/* Linea di partenza (tratteggio ciano sotto il muso) */}
        <line x1="150" y1="56" x2="150" y2="74" stroke={S} strokeWidth="2" strokeDasharray="3 3" opacity="0.5" />

        {/* Scie di velocita' (compaiono allo scatto, dietro l'auto) */}
        {loop && (
          <motion.g
            stroke={S}
            strokeLinecap="round"
            fill="none"
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0, 0, 0.95, 0.4, 0, 0] }}
            transition={{ duration: LOOP, times: [0, 0.2, 0.28, 0.4, 0.6, 0.78, 1], repeat: Infinity, ease: "easeOut" }}
          >
            <line x1="14" y1="40" x2="44" y2="40" strokeWidth="2.4" opacity="0.85" />
            <line x1="8" y1="48" x2="34" y2="48" strokeWidth="2" opacity="0.6" />
            <line x1="18" y1="56" x2="40" y2="56" strokeWidth="1.6" opacity="0.4" />
          </motion.g>
        )}

        {/* Monoposto: scatta a destra (translate x del gruppo) con micro-vibrazione */}
        <motion.g
          initial={false}
          animate={loop ? { x: carX, opacity: carOpacity } : { x: 0, opacity: 1 }}
          transition={
            loop
              ? { duration: LOOP, times: carTimes, repeat: Infinity, ease: [0.45, 0, 0.25, 1] }
              : { duration: 0 }
          }
        >
          {/* --- Telaio in line-art ciano. Auto rivolta a DESTRA. ---
              Ruota posteriore ~x=66, anteriore ~x=126, muso fino a ~150. */}

          {/* Ala posteriore (montante + piano) */}
          <line x1="50" y1="40" x2="50" y2="56" stroke={S} strokeWidth="2.4" strokeLinecap="round" />
          <line x1="44" y1="40" x2="58" y2="40" stroke={S} strokeWidth="3" strokeLinecap="round" />
          <line x1="46" y1="35.5" x2="56" y2="35.5" stroke={S} strokeWidth="2" strokeLinecap="round" opacity="0.7" />

          {/* Fondo + corpo: cofano motore -> abitacolo -> muso affusolato */}
          <path
            d="M52 56
               L58 56
               C62 50 70 48 78 48
               L88 47
               C94 44 100 43 108 43
               L120 45
               C134 46 144 49 150 53
               L150 56 Z"
            fill={S}
            fillOpacity="0.16"
            stroke={S}
            strokeWidth="2.2"
            strokeLinejoin="round"
          />

          {/* Pance / presa d'aria laterale */}
          <path d="M86 50 L104 49 L110 53 L88 53 Z" fill={S} fillOpacity="0.28" />

          {/* Cono muso (punta) */}
          <path d="M148 53 L150 53 L150 55 L148 55 Z" fill={S} />

          {/* Ala anteriore */}
          <line x1="148" y1="56" x2="160" y2="56" stroke={S} strokeWidth="3" strokeLinecap="round" />
          <line x1="150" y1="59" x2="160" y2="59" stroke={S} strokeWidth="2" strokeLinecap="round" opacity="0.7" />
          <line x1="160" y1="53.5" x2="160" y2="60.5" stroke={S} strokeWidth="2" strokeLinecap="round" />

          {/* Halo + abitacolo */}
          <path d="M84 47 C84 41 96 41 96 47" fill="none" stroke={S} strokeWidth="2" strokeLinecap="round" opacity="0.85" />
          <circle cx="90" cy="44" r="3.4" fill={SURF} stroke={S} strokeWidth="1.8" />

          {/* Roll hoop / airbox dietro il pilota */}
          <path d="M78 48 C76 42 82 41 84 46" fill="none" stroke={S} strokeWidth="2" strokeLinecap="round" opacity="0.8" />

          {/* Ruota posteriore */}
          <motion.g
            initial={false}
            animate={loop ? { rotate: wheelSpin } : { rotate: 0 }}
            transition={loop ? { duration: LOOP, times: wheelTimes, repeat: Infinity, ease: [0.45, 0, 0.25, 1] } : { duration: 0 }}
            style={{ transformOrigin: "66px 58px" }}
          >
            <circle cx="66" cy="58" r="8" fill="hsl(var(--foreground))" />
            <circle cx="66" cy="58" r="8" fill="none" stroke={S} strokeWidth="1.6" />
            <circle cx="66" cy="58" r="3" fill={S} />
            <line x1="66" y1="51.5" x2="66" y2="64.5" stroke={SURF} strokeWidth="1.2" opacity="0.7" />
            <line x1="59.5" y1="58" x2="72.5" y2="58" stroke={SURF} strokeWidth="1.2" opacity="0.7" />
          </motion.g>

          {/* Ruota anteriore */}
          <motion.g
            initial={false}
            animate={loop ? { rotate: wheelSpin } : { rotate: 0 }}
            transition={loop ? { duration: LOOP, times: wheelTimes, repeat: Infinity, ease: [0.45, 0, 0.25, 1] } : { duration: 0 }}
            style={{ transformOrigin: "126px 58px" }}
          >
            <circle cx="126" cy="58" r="8" fill="hsl(var(--foreground))" />
            <circle cx="126" cy="58" r="8" fill="none" stroke={S} strokeWidth="1.6" />
            <circle cx="126" cy="58" r="3" fill={S} />
            <line x1="126" y1="51.5" x2="126" y2="64.5" stroke={SURF} strokeWidth="1.2" opacity="0.7" />
            <line x1="119.5" y1="58" x2="132.5" y2="58" stroke={SURF} strokeWidth="1.2" opacity="0.7" />
          </motion.g>
        </motion.g>
      </svg>
      <span className="mt-1 block text-center font-mono text-[0.65rem] uppercase tracking-[0.18em] text-primary">
        {isItalian ? "Pronti, via" : "Ready, set, go"}
      </span>
    </div>
  );
}

export function AbbonamentoContent() {
  const locale = useLocale();
  const isItalian = locale.startsWith("it");
  const routes = localizedRoutes[isItalian ? "it" : "en"];
  const bookingLink = `${routes.contacts}#tidycal`;
  const [selectedPlan, setSelectedPlan] = useState<PlanKey>("monthly");

  const plans = isItalian
    ? {
        monthly: {
          label: "Mensile",
          badge: "Flessibile",
          price: "€ 4.800",
          unit: "/ mese",
          note: "Piano continuativo per evolvere prodotto e performance.",
          cta: "Prenota una call",
          includes: [
            "1 task attivo per volta (coda illimitata)",
            "Sviluppo full-stack su priorità condivise",
            "Revisioni sulla richiesta originale",
            "Comunicazione asincrona prioritaria",
          ],
        },
        weekly: {
          label: "Settimanale",
          badge: "Sprint focus",
          price: "€ 1.400",
          unit: "/ settimana",
          note: "Sprint mirato per avanzare rapidamente su un obiettivo.",
          cta: "Prenota una call",
          includes: [
            "Sprint tecnico ad alta priorità",
            "Consegna rapida su scope definito",
            "Supporto async durante la settimana",
            "Aggiornamenti scritti giornalieri",
          ],
        },
        consulting: {
          label: "Consulenza",
          badge: "1:1 operativo",
          price: "€ 110",
          unit: "/ ora",
          note: "Assistenza tecnica e consulenza strategica operativa.",
          cta: "Prenota consulenza",
          includes: [
            "Analisi tecnica e piano operativo",
            "Revisione architettura e codice",
            "Call 1:1 con prossimi step concreti",
            "Sintesi scritta con priorità",
          ],
        },
      }
    : {
        monthly: {
          label: "Monthly",
          badge: "Flexible",
          price: "€ 4,800",
          unit: "/ month",
          note: "Continuous plan to improve product and performance.",
          cta: "Book a call",
          includes: [
            "1 active task at a time (unlimited queue)",
            "Full-stack development on shared priorities",
            "Revisions on the original request",
            "Priority async communication",
          ],
        },
        weekly: {
          label: "Weekly",
          badge: "Sprint focus",
          price: "€ 1,400",
          unit: "/ week",
          note: "Focused sprint to move quickly on one goal.",
          cta: "Book a call",
          includes: [
            "High-priority technical sprint",
            "Fast delivery on a defined scope",
            "Async support during the week",
            "Daily written updates",
          ],
        },
        consulting: {
          label: "Consulting",
          badge: "1:1 advisory",
          price: "€ 110",
          unit: "/ hour",
          note: "Technical assistance and strategic consulting.",
          cta: "Book consulting",
          includes: [
            "Technical analysis and action plan",
            "Architecture and code review",
            "1:1 call with concrete next steps",
            "Written summary with priorities",
          ],
        },
      };

  const quarterly = isItalian
    ? {
        badge: "Risparmi 1.800 € sul trimestre",
        title: "Preferisci impegnarti?",
        price: "€ 4.200",
        unit: "/ mese",
        save: "600 € in meno al mese rispetto al mensile flessibile.",
        how: "Impegno di 3 mesi: se interrompi prima, i canoni fino a fine trimestre restano dovuti. Canone mensile anticipato: paghi all'inizio di ogni mese, non a 30 giorni. A fine periodo decidi se rinnovare.",
      }
    : {
        badge: "Save €1,800 per quarter",
        title: "Ready to commit?",
        price: "€ 4,200",
        unit: "/ month",
        save: "€600/month less than the flexible monthly.",
        how: "3-month commitment: if you stop early, the fees up to the end of the quarter remain due. Monthly fee paid in advance: you pay at the start of each month, not 30 days later. At the end of the period you decide whether to renew.",
      };

  const activePlan = plans[selectedPlan];

  const rules: FlowStep[] = isItalian
    ? [
        {
          icon: RefreshCcw,
          index: "1",
          title: "1 task attivo per volta",
          descriptionTop:
            "Lavoro su una richiesta alla volta, con focus totale.",
          descriptionBottom:
            "La coda è illimitata: puoi aggiungere nuove richieste in qualsiasi momento.",
        },
        {
          icon: Code2,
          index: "2",
          title: "Comunicazione asincrona",
          descriptionTop: "Niente call obbligatorie o passaggi superflui.",
          descriptionBottom:
            "Aggiornamenti scritti, tracciabili, con risposte rapide.",
        },
        {
          icon: FileText,
          index: "3",
          title: "Consegna pronta",
          descriptionTop: "Ogni task arriva pronto per rilascio e utilizzo.",
          descriptionBottom: "Documentazione operativa inclusa quando serve.",
        },
        {
          icon: TestTube,
          index: "4",
          title: "Revisioni sulla richiesta",
          descriptionTop:
            "Rifiniamo finché l'obiettivo definito non è centrato.",
          descriptionBottom: "Niente consegne lasciate a metà.",
        },
      ]
    : [
        {
          icon: RefreshCcw,
          index: "1",
          title: "1 active task at a time",
          descriptionTop: "I work on one request at a time with full focus.",
          descriptionBottom:
            "Queue is unlimited: you can add requests anytime.",
        },
        {
          icon: Code2,
          index: "2",
          title: "Asynchronous communication",
          descriptionTop: "No mandatory calls or unnecessary layers.",
          descriptionBottom: "Written, traceable updates with fast replies.",
        },
        {
          icon: FileText,
          index: "3",
          title: "Ready-to-ship delivery",
          descriptionTop: "Each task is delivered ready to release and use.",
          descriptionBottom: "Operational documentation included when needed.",
        },
        {
          icon: TestTube,
          index: "4",
          title: "Request-focused revisions",
          descriptionTop: "We refine until the defined goal is met.",
          descriptionBottom: "No half-finished delivery.",
        },
      ];

  const included = isItalian
    ? [
        "Richieste illimitate (1 attiva per volta)",
        "Revisioni sulla richiesta originale",
        "Comunicazione prioritaria",
        "Task frontend e backend applicativo",
        "Bug fixing e miglioramenti continui",
        "Documentazione essenziale",
      ]
    : [
        "Unlimited requests (1 active at a time)",
        "Revisions on the original request",
        "Priority communication",
        "Frontend and app-backend tasks",
        "Bug fixing and continuous improvements",
        "Essential documentation",
      ];

  const excluded = isItalian
    ? [
        "Gestione contenuti CMS redazionale",
        "Redesign completi senza priorità",
        "Progetti enterprise fuori perimetro concordato",
        "Supporto operativo 24/7",
      ]
    : [
        "Editorial CMS content operations",
        "Full redesigns without prioritization",
        "Enterprise projects outside agreed scope",
        "24/7 operational support",
      ];

  const examples = isItalian
    ? [
        {
          title: "Riduzione tempi di caricamento homepage",
          type: "Performance",
        },
        { title: "Refactoring flusso checkout e form", type: "Frontend" },
        { title: "Integrazione Stripe e webhook", type: "Backend" },
        { title: "Dashboard KPI con Supabase", type: "Data" },
        { title: "Setup logging errori e alert", type: "Affidabilità" },
        { title: "Migliorie SEO tecnico su pagine core", type: "SEO tecnico" },
        { title: "Ottimizzazione query PostgreSQL", type: "Database" },
        { title: "Hardening base autenticazione API", type: "Sicurezza" },
      ]
    : [
        { title: "Homepage loading time reduction", type: "Performance" },
        { title: "Checkout and form flow refactor", type: "Frontend" },
        { title: "Stripe and webhook integration", type: "Backend" },
        { title: "KPI dashboard with Supabase", type: "Data" },
        { title: "Error logging and alerts setup", type: "Reliability" },
        { title: "Core technical SEO improvements", type: "Technical SEO" },
        { title: "PostgreSQL query optimization", type: "Database" },
        { title: "API auth baseline hardening", type: "Security" },
      ];

  const terms = isItalian
    ? [
        {
          label: "Modalità operativa",
          text: "Asincrona (call rapide di circa 15 minuti solo quando servono, per lo più da remoto e in forma scritta). Ogni richiesta ha briefing, stima e aggiornamenti scritti.",
        },
        {
          label: "Capacità",
          text: "Una richiesta attiva per volta. Coda illimitata e priorità condivisa.",
        },
        {
          label: "Revisioni",
          text: "Sulla richiesta originale, entro la finestra concordata dopo consegna.",
        },
        {
          label: "Perimetro",
          text: "Solo codice. Non include gestione contenuti CMS o attività redazionali.",
        },
        {
          label: "Pagamenti e rinnovo",
          text: "Canone anticipato. Stop o pausa possibili al rinnovo successivo.",
        },
        {
          label: "Trimestrale (opzionale)",
          text: "4.200 € al mese con impegno di 3 mesi (risparmi 1.800 € sul trimestre). Se interrompi prima, i canoni fino a fine trimestre restano dovuti. Canone mensile anticipato; a fine periodo decidi se rinnovare.",
        },
        {
          label: "Ownership",
          text: "Codice e asset custom restano al cliente al saldo delle competenze.",
        },
      ]
    : [
        {
          label: "Operating mode",
          text: "Asynchronous (quick calls of about 15 minutes only when needed, mostly remote and in writing). Every request includes briefing, estimate, and written updates.",
        },
        {
          label: "Capacity",
          text: "One active request at a time. Unlimited queue and shared prioritization.",
        },
        {
          label: "Revisions",
          text: "On the original request, within the agreed delivery window.",
        },
        {
          label: "Scope",
          text: "Code only. No CMS content management or editorial activities.",
        },
        {
          label: "Payments and renewal",
          text: "Fee paid in advance. Stop or pause at next renewal.",
        },
        {
          label: "Quarterly (optional)",
          text: "€4,200/month with a 3-month commitment (save €1,800 per quarter). If you stop early, the fees up to the end of the quarter remain due. Monthly fee paid in advance; at the end you decide whether to renew.",
        },
        {
          label: "Ownership",
          text: "Custom code and assets remain client property after final payment.",
        },
      ];

  return (
    <>
      <section className="section-padding pt-8 md:pt-24 xl:min-h-[calc(100dvh-5.5rem)] xl:flex xl:flex-col xl:justify-center xl:py-0 relative overflow-hidden">
        <div className="absolute inset-0 pattern-dots" />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl" />

        <div className="container-wide relative">
          <div className="grid md:grid-cols-2 gap-10 md:gap-8 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="max-w-2xl text-left"
            >
              <span className="font-mono text-label text-primary mb-4 block">
                {isItalian
                  ? "Full-stack in abbonamento"
                  : "Subscription full-stack developer"}
              </span>
              <h1 className="page-hero-title mb-6">
                {isItalian ? (
                  <>
                    Il tuo full-stack developer che fa crescere il tuo{" "}
                    <span className="text-shine">prodotto</span>
                  </>
                ) : (
                  <>
                    Your developer of trust, growing your{" "}
                    <span className="text-shine">product</span>.
                  </>
                )}
              </h1>
              <p className="text-body-lg text-muted-foreground max-w-2xl mx-0 leading-relaxed">
                {isItalian ? (
                  <>
                    <span className="block sm:inline font-medium text-foreground whitespace-nowrap">
                      5 anni di esperienza.
                    </span>
                    <span className="block sm:inline mt-1 sm:mt-0">
                      {" "}
                      Accesso diretto su{" "}
                    </span>
                    <span className="font-medium text-foreground">
                      codice, performance e integrazioni
                    </span>
                    , con{" "}
                    <span className="font-medium text-primary">
                      crescita continua del prodotto
                    </span>
                    .
                  </>
                ) : (
                  <>
                    <span className="font-medium text-foreground">
                      5 years of experience.
                    </span>{" "}
                    Direct access for{" "}
                    <span className="font-medium text-foreground">
                      code, performance, and integrations
                    </span>
                    , with{" "}
                    <span className="font-medium text-primary">
                      continuous product growth
                    </span>
                    .
                  </>
                )}
              </p>
            </motion.div>

            <div className="flex justify-center items-center">
              <AbbonamentoHeroVisual isItalian={isItalian} />
            </div>
          </div>
        </div>
      </section>

      <InBreve
        className="pb-20 md:pb-28 lg:pb-28"
        text={
          isItalian
            ? "L'abbonamento è uno sviluppatore dedicato al tuo prodotto ogni mese, a quota fissa. Lavoro su una richiesta alla volta, con coda illimitata e comunicazione semplice e diretta. Niente assunzioni e niente sorprese in fattura: paghi una cifra chiara e il prodotto avanza ogni settimana. 4.800 € al mese, oppure 4.200 € al mese col trimestrale (impegno 3 mesi, risparmi 1.800 €), o 1.400 € a settimana; col mensile puoi mettere in pausa o cambiare piano al rinnovo."
            : "The subscription is a developer dedicated to your product every month, at a fixed rate. I work on one request at a time, with an unlimited queue and simple, direct communication. No hiring and no surprises on the invoice: you pay a clear amount and the product moves forward every week. 4.800 € a month, or 4.200 € a month with the quarterly plan (3-month commitment, save 1.800 €), or 1.400 € a week; with the monthly plan you can pause or change plan at renewal."
        }
        aside={<InBreveAside isItalian={isItalian} />}
      />

      <section className="py-20 md:py-28 lg:py-28 bg-surface/30">
        <div className="container-wide">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-12 text-left"
          >
            <span className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3.5 py-1.5 font-mono text-xs uppercase tracking-[0.12em] text-primary">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              {isItalian ? "Listino trasparente" : "Transparent pricing"}
            </span>
            <h2 className="font-display text-[2.1rem] font-bold leading-tight md:text-5xl">
              {isItalian ? (
                <>
                  Prezzo fisso,{" "}
                  <span className="text-shine">senza sorprese</span>
                </>
              ) : (
                <>
                  Fixed price,{" "}
                  <span className="text-shine">no surprises</span>
                </>
              )}
            </h2>
            <p className="mt-5 max-w-xl text-body-lg text-muted-foreground">
              {isItalian
                ? "Mensile, settimanale o consulenza: scegli la formula giusta per il tuo momento."
                : "Monthly, weekly, or consulting: pick the format for your current stage."}
            </p>
          </motion.div>

          <div className="grid xl:grid-cols-[0.8fr_1.2fr] gap-6">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="surface-card flex flex-col p-6 md:p-8"
            >
              <div className="flex justify-start">
                <span className="inline-flex items-center gap-2 rounded-full border border-primary/35 bg-primary/10 px-3 py-1.5 text-sm font-medium text-primary">
                  <span className="h-2 w-2 rounded-full bg-primary" />
                  {isItalian
                    ? "Disponibile per nuovi task"
                    : "Available for new tasks"}
                </span>
              </div>

              <div className="mt-6 space-y-3 text-sm text-muted-foreground text-left">
                <p className="max-w-2xl mx-0">
                  {isItalian
                    ? "Lavori con me direttamente: full-stack developer con approccio pratico e misurabile."
                    : "You work directly with me: full-stack developer with a practical, measurable approach."}
                </p>
                <p className="max-w-2xl mx-0">
                  {isItalian
                    ? "Niente passaggi intermedi, solo priorità chiare e consegne concrete."
                    : "No extra layers, only clear priorities and concrete delivery."}
                </p>
              </div>

              <div className="mt-6 rounded-xl border border-[hsl(var(--border))] bg-surface-hover/40 p-4 md:p-5">
                <div className="flex flex-col items-start gap-3 text-left sm:flex-row sm:items-center">
                  <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center shrink-0">
                    <MessageSquare className="w-5 h-5 text-primary" />
                  </div>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {isItalian ? (
                      <>
                        Hai dubbi sul modello in abbonamento?{" "}
                        <span className="font-semibold text-foreground">
                          Facciamo una call
                        </span>{" "}
                        e valutiamo subito{" "}
                        <span className="font-semibold text-primary">
                          se c&apos;è il fit giusto
                        </span>
                        .
                      </>
                    ) : (
                      <>
                        Not sure about the subscription model?{" "}
                        <span className="font-semibold text-foreground">
                          Let&apos;s do a call
                        </span>{" "}
                        and quickly validate{" "}
                        <span className="font-semibold text-primary">
                          whether the fit is right
                        </span>
                        .
                      </>
                    )}
                  </p>
                </div>
              </div>

              <DeliveryRhythm isItalian={isItalian} />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="surface-card p-6 md:p-8 text-center"
            >
              <h3 className="font-display text-3xl md:text-[2.35rem] font-semibold mb-5">
                {isItalian
                  ? "Scegli il ritmo di lavoro"
                  : "Choose your work rhythm"}
              </h3>

              <div className="mb-8 grid grid-cols-3 gap-1 rounded-2xl border border-[hsl(var(--border))] bg-surface-hover/50 p-1 sm:gap-2">
                {(["monthly", "weekly", "consulting"] as const).map((key) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setSelectedPlan(key)}
                    className={`min-w-0 min-h-11 rounded-xl px-1.5 py-2.5 sm:px-3 text-[12px] sm:text-sm font-semibold leading-tight transition-colors ${
                      selectedPlan === key
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <span className="flex min-h-[3.25rem] flex-col items-center justify-center text-center">
                      <span className="whitespace-nowrap">{plans[key].label}</span>
                      {plans[key].badge ? (
                        <span
                          className={`mt-1 rounded-full px-2 py-0.5 text-[9px] sm:text-[11px] leading-none font-medium tracking-[0.06em] whitespace-nowrap ${
                            selectedPlan === key
                              ? "bg-primary-foreground/15 text-primary-foreground/90"
                              : "bg-primary/10 text-primary/90"
                          }`}
                        >
                          {plans[key].badge}
                        </span>
                      ) : null}
                    </span>
                  </button>
                ))}
              </div>

              <div className="flex flex-wrap items-end justify-center gap-2 mb-4">
                <span className="font-display text-5xl md:text-6xl font-bold leading-none">
                  {activePlan.price}
                </span>
                <span className="text-2xl md:text-3xl font-semibold text-primary leading-none mb-1">
                  {activePlan.unit}
                </span>
              </div>
              <p className="text-sm text-muted-foreground mb-6 max-w-xl mx-auto">
                {activePlan.note}
              </p>

              <div className="mb-8 flex justify-center">
                <Button variant="hero" size="lg" className="w-full sm:w-auto" asChild>
                  <Link
                    href={bookingLink}
                    onClick={() =>
                      trackEvent("book_call_click", {
                        tool: "calendly",
                        location: "subscription_pricing",
                        plan: selectedPlan,
                      })
                    }
                  >
                    {activePlan.cta}
                    <PhoneCall className="w-4 h-4" />
                  </Link>
                </Button>
              </div>

              <div className="mx-auto grid max-w-xl grid-cols-1 gap-3 sm:grid-cols-2">
                {activePlan.includes.map((item) => (
                  <div
                    key={item}
                    className="grid grid-cols-[20px_minmax(0,1fr)] items-start gap-3 rounded-xl border border-[hsl(var(--border))] bg-surface-hover/30 p-3.5 text-left"
                  >
                    <div className="mt-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary/20">
                      <Check className="h-3 w-3 text-primary" />
                    </div>
                    <span className="text-sm leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>

              {selectedPlan === "monthly" && (
                <div className="mx-auto mt-6 max-w-xl rounded-2xl border border-primary/30 bg-primary/[0.06] p-5 text-left">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-1 text-[11px] font-medium text-primary">
                      <CalendarClock className="h-3.5 w-3.5" />
                      {quarterly.badge}
                    </span>
                    <span className="font-display text-2xl font-bold leading-none">
                      {quarterly.price}
                      <span className="ml-1 text-base font-semibold text-primary">
                        {quarterly.unit}
                      </span>
                    </span>
                  </div>
                  <p className="mb-1 text-sm font-semibold">
                    {quarterly.title} {quarterly.save}
                  </p>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {quarterly.how}{" "}
                    <Link
                      href={routes.terms}
                      className="font-medium text-foreground underline underline-offset-4 decoration-primary/50 hover:text-primary transition-colors"
                    >
                      {isItalian ? "Condizioni complete nei termini" : "Full conditions in the terms"}
                    </Link>
                    .
                  </p>
                </div>
              )}
            </motion.div>
          </div>

          <p className="mt-6 text-left text-xs text-muted-foreground">
            {isItalian
              ? "Mensile: metti in pausa o interrompi al rinnovo successivo. Trimestrale: impegno di 3 mesi, poi rinnovo su richiesta."
              : "Monthly: pause or stop at the next renewal. Quarterly: 3-month commitment, then renewal on request."}
          </p>
        </div>
      </section>

      <CostComparison isItalian={isItalian} />

      <TaskFlow isItalian={isItalian} steps={rules} />

      <ProcessDemo />

      <section className="py-20 md:py-28 lg:py-28">
        <div className="container-wide">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-10 text-left"
          >
            <span className="font-mono text-label text-primary mb-4 block">
              {isItalian ? "Dentro e fuori perimetro" : "In and out of scope"}
            </span>
            <h2 className="font-display text-h2 font-bold">
              {isItalian ? "Cosa rientra, cosa no" : "What's in, what's out"}
            </h2>
          </motion.div>

          {/* Motif signature: due lati su un'unica superficie, con asta centrale ciano */}
          <div className="grid max-w-5xl overflow-hidden rounded-2xl border border-[hsl(var(--border))] bg-surface md:grid-cols-2">
            {/* Lato incluso (rail ciano a sinistra) */}
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="relative p-8 md:p-10"
            >
              <span className="absolute inset-y-6 left-0 w-[3px] rounded-full bg-gradient-to-b from-primary to-primary-glow shadow-[0_0_18px_hsl(var(--primary)/0.5)]" />
              <h3 className="font-display text-xl font-semibold mb-6 flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/15">
                  <Check className="h-4 w-4 text-primary" />
                </span>
                {isItalian ? "Incluso" : "Included"}
              </h3>
              <ul className="space-y-3.5">
                {included.map((item, index) => (
                  <motion.li
                    key={item}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.35, delay: 0.05 * index }}
                    className="flex items-start gap-3"
                  >
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span className="text-sm">{item}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            {/* Lato escluso (muto, divisore centrale) - hover diventa rosso */}
            <motion.div
              initial={{ opacity: 0, x: 24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="group/excl relative border-t border-[hsl(var(--border))] bg-surface-hover/30 p-8 transition-colors duration-300 hover:border-[hsl(var(--destructive)/0.45)] md:border-l md:border-t-0 md:p-10"
            >
              {/* Rail rosso che si rivela sul lato all'hover (no layout shift) */}
              <span className="pointer-events-none absolute inset-y-6 left-0 w-[3px] rounded-full bg-[hsl(var(--destructive))] opacity-0 transition-opacity duration-300 group-hover/excl:opacity-70" />
              <h3 className="font-display text-xl font-semibold mb-6 flex items-center gap-2 text-muted-foreground transition-colors duration-300 group-hover/excl:text-[hsl(var(--destructive))]">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-foreground/[0.06] transition-colors duration-300 group-hover/excl:bg-[hsl(var(--destructive)/0.12)]">
                  <X className="h-4 w-4 text-muted-foreground transition-colors duration-300 group-hover/excl:text-[hsl(var(--destructive))]" />
                </span>
                {isItalian ? "Non incluso" : "Not included"}
              </h3>
              <ul className="space-y-3.5">
                {excluded.map((item, index) => (
                  <motion.li
                    key={item}
                    initial={{ opacity: 0, x: 10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.35, delay: 0.05 * index }}
                    className="flex items-start gap-3"
                  >
                    <X className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground/60 transition-colors duration-300 group-hover/excl:text-[hsl(var(--destructive)/0.7)]" />
                    <span className="text-sm text-muted-foreground transition-colors duration-300 group-hover/excl:text-[hsl(var(--destructive)/0.85)]">
                      {item}
                    </span>
                  </motion.li>
                ))}
              </ul>
              <p className="mt-6 text-xs text-muted-foreground">
                {isItalian
                  ? "Per progetti complessi, valuta il servizio"
                  : "For complex projects, consider"}{" "}
                <Link
                  href={routes.mvp}
                  className="underline hover:text-primary transition-colors"
                >
                  MVP Custom
                </Link>
                .
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <TaskLog isItalian={isItalian} items={examples} />

      <section className="py-20 md:py-28 lg:py-28 bg-surface/30">
        <div className="container-wide">
          <div className="grid gap-12 lg:grid-cols-[minmax(0,1fr)_22rem] lg:items-center lg:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="min-w-0"
          >
            <h2 className="font-display text-2xl md:text-[2rem] xl:text-h3 font-bold mb-6 text-left">
              {isItalian
                ? "Termini servizio abbonamento"
                : "Subscription terms"}
            </h2>

            <Accordion
              type="single"
              collapsible
              className="divide-y divide-[hsl(var(--border))] overflow-hidden rounded-2xl border border-[hsl(var(--border))] bg-surface"
            >
              {terms.map((term) => (
                <AccordionItem
                  key={term.label}
                  value={term.label}
                  className="border-none"
                >
                  <AccordionTrigger className="group px-5 py-4 text-left hover:no-underline [&>svg]:hidden">
                    <span className="flex w-full items-center gap-3">
                      <span className="font-mono text-xs uppercase tracking-[0.08em] text-primary">
                        {term.label}
                      </span>
                      <ChevronDown className="ml-auto h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-180" />
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="px-5 pb-4 pt-0 text-sm text-muted-foreground">
                    {term.text}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <p className="mt-6 text-sm md:text-[0.98rem] text-muted-foreground text-left">
              {isItalian
                ? "I dettagli legali completi restano nei"
                : "Full legal details are available in"}{" "}
              <Link
                href={routes.terms}
                className="font-medium text-foreground underline underline-offset-4 decoration-primary/50 hover:text-primary transition-colors"
              >
                {isItalian ? "Termini e Condizioni" : "Terms & Conditions"}
              </Link>
              .
            </p>
          </motion.div>
          <div className="hidden lg:flex lg:items-center lg:justify-center">
            <TermsCycleVisual isItalian={isItalian} />
          </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 lg:py-28">
        <div className="container-wide">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="surface-card mx-auto max-w-3xl p-8 text-center md:p-10"
          >
            <RaceCarVisual isItalian={isItalian} />
            <h2 className="page-cta-title mb-4">
              {isItalian ? "Vuoi partire ora?" : "Ready to start now?"}
            </h2>
            <p className="mx-auto mb-8 max-w-lg text-body-lg text-muted-foreground">
              {isItalian
                ? "Prenota una call: allineiamo priorità, tempi e prossime consegne."
                : "Book a call: align priorities, timing, and next deliveries."}
            </p>
            <Button variant="hero" size="xl" className="w-full sm:w-auto" asChild>
              <Link
                href={bookingLink}
                onClick={() =>
                  trackEvent("book_call_click", {
                    tool: "calendly",
                    location: "cta_section",
                  })
                }
              >
                {isItalian ? "Prenota una call" : "Book a call"}
                <PhoneCall className="w-4 h-4" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </>
  );
}
